/**
 * Workspace toolbar for consistent headers across workspace pages.
 * Props: title, subtitle?, leftSlot?, rightSlot?, breadcrumbs?, sticky?
 * Uses theme tokens for border, background, spacing, and optional sticky.
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface WorkspaceToolbarProps {
  /** Main title (e.g. "Reports", "Activities"). */
  title: string;
  /** Optional subtitle or context (e.g. "Preview", project name). */
  subtitle?: string | null;
  /** Optional left slot (back button, breadcrumbs, search, etc.). */
  leftSlot?: React.ReactNode;
  /** Optional right slot (actions, view toggles, etc.). */
  rightSlot?: React.ReactNode;
  /** Optional breadcrumbs (array of { label, href? } or nodes). */
  breadcrumbs?: React.ReactNode;
  /** When true, toolbar sticks to top on scroll. Default false. */
  sticky?: boolean;
  /** Extra class for the toolbar container. */
  className?: string;
}

const TOOLBAR_HEIGHT = 'h-14';
const PADDING = 'px-6 py-3';

export const WorkspaceToolbar: React.FC<WorkspaceToolbarProps> = ({
  title,
  subtitle,
  leftSlot,
  rightSlot,
  breadcrumbs,
  sticky = false,
  className,
}) => {
  return (
    <header
      className={cn(
        'flex-none flex flex-col justify-center border-b border-border bg-card',
        TOOLBAR_HEIGHT,
        PADDING,
        sticky && 'sticky top-0 z-10',
        className
      )}
      data-testid="workspace-toolbar"
    >
      <div className="flex items-center justify-between gap-4 min-h-0">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          {leftSlot != null && (
            <div className="flex items-center gap-2 shrink-0 h-9">
              {leftSlot}
            </div>
          )}
          {(title != null && title !== '') || breadcrumbs != null ? (
            <div className="min-w-0 flex-1">
              {breadcrumbs != null && (
                <div className="text-xs text-muted-foreground mb-0.5">
                  {breadcrumbs}
                </div>
              )}
              {title != null && title !== '' && (
                <>
                  <h1 className="text-lg font-semibold text-foreground truncate">
                    {title}
                  </h1>
                  {subtitle != null && subtitle !== '' && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {subtitle}
                    </p>
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>
        {rightSlot != null && (
          <div className="flex items-center gap-2 flex-wrap justify-end min-h-9">
            {rightSlot}
          </div>
        )}
      </div>
    </header>
  );
};
